import type { SupportBinding } from './supportTransport';
import { adviserRequest } from '../advisers/api';

export type AdminScope = 'user' | 'company' | 'practice';
export interface AdminPerson {
  id: string;
  clerkId: string;
  name: string;
  email: string;
  photo: string | null;
  title: string | null;
  superUser: boolean;
  isFounder: boolean;
  founderSubscriptionStatus: string | null;
  createdAt: string;
}
export interface AdminTenant {
  id: string;
  name: string;
  clerkOrgId: string;
  industry: string | null;
  size: string | null;
  logo: string | null;
  isDemo: boolean;
  subscriptionTier: string;
  billingStatus: string;
  provisionalPracticeId: string | null;
  demoPracticeId: string | null;
  demoOwnerUserId: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface AdminPractice {
  id: string;
  name: string;
  ownerUserId: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  website: string | null;
  reviewReason: string | null;
  createdAt: string;
  profile: null | {
    slug: string | null;
    reviewStage: 'draft' | 'submitted' | 'approved' | 'changes_requested';
    isPublished: boolean;
    publiclyListed: boolean;
    publishedAt: string | null;
    featuredOrder: number | null;
  };
}
export interface AdminMembership {
  id: string;
  companyId: string;
  userId: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  adviserPracticeId: string | null;
  joinedAt: string;
  active: boolean;
}
export interface AdminRelationships {
  overview: {
    users: number;
    companies: number;
    practices: number;
    approvedPractices: number;
    pendingApplications: number;
    suspendedPractices: number;
    publicProfiles: number;
    featuredProfiles: number;
    demoCompanies: number;
  };
  users: AdminPerson[];
  companies: AdminTenant[];
  practices: AdminPractice[];
  memberships: AdminMembership[];
  practiceMembers: Array<{
    id: string;
    practiceId: string;
    userId: string;
    role: 'owner' | 'staff';
    createdAt: string;
  }>;
  clientLinks: Array<{
    id: string;
    practiceId: string;
    companyId: string;
    status: string;
    createdBy: string;
    acceptedBy: string | null;
    acceptedAt: string | null;
    revokedAt: string | null;
    createdAt: string;
  }>;
  assignments: Array<{
    id: string;
    practiceId: string;
    companyId: string;
    userId: string;
    assignedBy: string;
    createdAt: string;
  }>;
}
export interface ImpersonationIdentity {
  id: string;
  clerkId: string;
  name: string;
  email: string;
}
export interface AdminImpersonation {
  mode: 'app' | 'clerk';
  id: string;
  actor: ImpersonationIdentity;
  target: ImpersonationIdentity;
  scope: AdminScope;
  scopeId: string | null;
  reason: string;
  startedAt: string;
  expiresAt: string;
  originalSessionId: string | null;
  redirectPath: string;
  returnUrl: string;
}
export interface ImpersonationStart {
  id: string;
  mode: 'app';
  credential: string;
  expiresAt: string;
  sessionExpiresAt: string;
  target: ImpersonationIdentity;
  scope: AdminScope;
  scopeId: string | null;
  redirectPath: string;
  returnUrl: string;
  originalSessionId: string | null;
}
export interface ImpersonationAudit {
  id: string;
  actorUserId: string;
  targetUserId: string;
  actorClerkUserId: string;
  targetClerkUserId: string;
  scope: AdminScope;
  scopeId: string | null;
  reason: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  expiresAt: string;
  error: string | null;
}
const post = (body: unknown): RequestInit => ({
  method: 'POST',
  body: JSON.stringify(body),
  cache: 'no-store',
});
export const adminWorkspaceApi = {
  relationships: () =>
    adviserRequest<AdminRelationships>('/api/admin/relationships', { cache: 'no-store' }),
  startImpersonation: (scope: AdminScope, scopeId: string, reason: string, targetUserId?: string) =>
    adviserRequest<ImpersonationStart>(
      '/api/admin/impersonation',
      post({ scope, scopeId, reason, ...(targetUserId && { targetUserId }) })
    ),
  abortImpersonation: (id: string, binding?: SupportBinding) =>
    adviserRequest<{ ended: true; id: string }>(
      `/api/admin/impersonation/${id}/abort`,
      post({}),
      false,
      binding
    ),
  currentImpersonation: () =>
    adviserRequest<{ active: boolean; impersonation: AdminImpersonation | null }>(
      '/api/admin/impersonation/current',
      { cache: 'no-store' }
    ),
  endImpersonation: () =>
    adviserRequest<{
      ended: true;
      returnUrl: string;
      resumeSessionId: string | null;
      signInUrl?: string;
      mode: 'app' | 'clerk';
      originalSessionPreserved?: boolean;
    }>('/api/admin/impersonation/end', post({})),
  impersonationHistory: () =>
    adviserRequest<ImpersonationAudit[]>('/api/admin/impersonation/history', { cache: 'no-store' }),
};
